<script lang="ts">
  import ImageCropper from "$app/components/ImageCropper.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { xhr, toast, catchError } from "$app/utils"
  import { Loader, Pen } from "lucide-svelte"
  import Button from "$app/components/ButtonSpinner.svelte"
  import { appStore } from "$app/store.svelte"
  import userImgJPG from "@images/users/default.jpg"

  const USER_FIELDS = [
    "name",
    "title",
    "display_name",
    "email",
    "mobile_number"
  ]
  let form = $state({ ...appStore.current_user })
  let errors = $state<Record<string, string>>({})
  let avatar: string = $state("")
  let avatarTmp: any = $state(null)
  let fileInput = $state<HTMLInputElement | null>(null)
  let fileImage = $state<File | null>(null)
  let avatarChanged = $state(false)
  let openImgCropper = $state(false)

  avatar = appStore.current_user.avatar_thumb_url
    ? appStore.current_user.avatar_thumb_url
    : ""

  // svelte-ignore state_referenced_locally
  if (avatar !== "") {
    const name = avatar.split("/").pop().split("#")[0].split("?")[0]
    if (name.match(/\.(jpg|jpeg)$/i)) {
      //fileImage = { type: "image/jpeg", name }
    } else {
      //fileImage = { type: "image/png", name }
    }
  }

  function syncAvatar(): void {
    const imgAvatar: HTMLImageElement = document.querySelector(
      ".main-nav .current-user-avatar"
    )
    if (imgAvatar) {
      imgAvatar.src = avatar
    }
  }

  const imgClass =
    "w-full h-full bg-gray-300 rounded-full shadow-lg flex-shrink-0"

  let saving = $state(false)
  async function updateUser() {
    saving = true
    errors = {}
    const data = {
      name: form.name,
      title: form.title,
      display_name: form.display_name,
      email: form.email,
      mobile_number: form.mobile_number,
      birthdate: form.birthdate
    }

    try {
      const resp = await xhr.patch(`/users/${appStore.current_user.id}.json`, {
        user: data
      })

      toast("Your settings have been updated.", "success")
      //onupdate(resp.data)
      appStore.current_user = resp.data
      if (avatarChanged) {
        syncAvatar()
      }
      avatarChanged = false
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating your settings", "error")
      if (e.response) {
        errors = { ...errors, ...e.response.data.errors }
      }
    } finally {
      saving = false
    }
  }

  let savingAvatar = $state(false)
  async function updateAvatar() {
    errors = {}
    const formData = new FormData()
    formData.append("user[avatar]", fileImage)

    const csrfElement = <HTMLInputElement>(
      document.getElementById("x-csrf-token")
    )
    if (!csrfElement || !csrfElement.value) {
      return
    }

    savingAvatar = true
    let [err, resp] = await catchError(
      xhr.patch(`/users/${appStore.current_user.id}`, formData)
    )

    if (err) {
      toast("There was an error updating your picture", "error")
      savingAvatar = false
      return
    }

    savingAvatar = false
    toast("Your picture has been updated.", "success")
    syncAvatar()
    avatarChanged = false
  }

  const onFileSelected = (e: Event) => {
    const target = e.target as HTMLInputElement
    fileImage = target.files[0]
    let reader = new FileReader()
    reader.readAsDataURL(fileImage)
    reader.onload = (e) => {
      avatarTmp = e.target.result

      openImgCropper = true
      avatarChanged = true
    }
  }
</script>

<div class="space-y-8">
  <div>
    <div class="w-full flex justify-center">
      <div class="space-y-1 text-center">
        <div class="mx-auto h-24 w-24 rounded-full bg-gray-100">
          {#if avatar}
            <img
              src={avatar}
              id="user-avatar"
              alt="User avatar"
              class={imgClass}
              data-name="avatar-image"
            />
          {:else}
            <img
              src={userImgJPG}
              alt="Select Avatar"
              class={imgClass}
              data-name="avatar-image"
            />
          {/if}
        </div>
        <div class="flex text-sm text-black text-center pt-2 justify-center">
          <button
            class="relative cursor-pointer bg-white rounded-md font-medium text-black hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
            type="button"
            onclick={() => {
              fileInput.click()
            }}
            disabled={savingAvatar}
          >
            {#if savingAvatar}
              <div class="flex items-center text-tertiary">
                <Loader class="animate-spin" />
                Updating Picture...
              </div>
            {:else}
              <div class="text-blue-500 flex items-center gap-1">
                <Pen class="size-4" />
                Replace Picture
              </div>
            {/if}
          </button>
        </div>
        <!-- {#if avatarChanged} -->
        {#if openImgCropper}
          <ImageCropper
            image={avatarTmp}
            open={openImgCropper}
            file={fileImage}
            cropShape="round"
            btnText="Update Picture"
            cropperClose={() => {
              openImgCropper = false
            }}
            imageCropped={(data) => {
              avatar = data.image
              fileImage = data.file
              appStore.current_user.has_avatar = true
              updateAvatar()
            }}
          />
        {/if}
        <p class="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
      </div>
      <input
        type="file"
        bind:this={fileInput}
        class="hidden"
        onchange={onFileSelected}
        accept=".jpg, .jpeg, .png"
      />
    </div>
    <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
      <div class="sm:col-span-3">
        <label for="name"> Full Name </label>
        <div class="mt-1 input-cont" class:error={errors.name}>
          <input type="text" bind:value={form.name} class="input w-full" />
          <div class="msg">{errors.name}</div>
        </div>
      </div>

      <div class="sm:col-span-3">
        <label for="title"> Title </label>
        <div class="mt-1 input-cont" class:error={errors.title}>
          <input type="text" bind:value={form.title} class="input w-full" />
        </div>
      </div>
      <div class="sm:col-span-3">
        <label for="email"> Birthday</label>
        <div class="mt-1 input-cont relative" class:error={errors.birthdate}>
          <input
            type="date"
            class="input placeholder-text-black p-2 border border-gray-300 w-full"
            bind:value={form.birthdate}
          />
          <div class="msg">{errors.birthdate}</div>
        </div>
      </div>
      <div class="sm:col-span-3">
        <label for="email"> Display Name </label>
        <div class="mt-1 input-cont" class:error={errors.display_name}>
          <input
            type="text"
            bind:value={form.display_name}
            class="input w-full"
          />
          <div class="msg">{errors.display_name}</div>
        </div>
      </div>
      <div class="sm:col-span-3">
        <label for="email"> Email address </label>
        <div class="mt-1 input-cont" class:error={errors.email}>
          <input type="email" bind:value={form.email} class="input w-full" />
          <div class="msg">{errors.email}</div>
        </div>
      </div>
      <div class="sm:col-span-3">
        <label for="email"> Mobile Phone Number </label>
        <div class="mt-1 input-cont" class:error={errors.mobile_number}>
          <input
            type="text"
            bind:value={form.mobile_number}
            class="input w-full"
          />
          <div class="msg">{errors.mobile_number}</div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-span-6 py-4 text-center">
    <Button
      onclick={updateUser}
      processing={saving}
      class="btn-rounded bg-green-600 hover:bg-green-700 text-white btn-big w-full text-lg font-bold md:w-60 h-14"
    >
      Continue
    </Button>
  </div>
</div>
